import type { Query, ResultSet } from '@cubejs-client/core'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useCubeLiveQuery } from '@/hooks/use-cube-live-query'
import {
  toNumber,
  type AgingWidget,
  type ChartWidget,
  type DashboardWidget,
  type KpiWidget,
  type ListWidget,
  type ReportSortItem,
  type TableWidget,
} from '@/lib/cube-report'
import { queryKeys } from '@/lib/query-keys'
import {
  agingQuery,
  chartWidgetQuery,
  groupedTableQuery,
  kpiQuery,
  listPageQuery,
  totalsQuery,
} from '@/services/cube/dashboard'
import { toRows, toTablePage, type ReportFilterState, type ReportRow } from '@/services/cube/report'
import { fetchFxRates } from '@/services/http/fx'

import { chartFromRows, kpiFromRows, listFromPage, tableFromRows } from './results'
import type { ChartResult, KpiResult, ListResult, TableResult, WidgetStatus } from './types'

/*
 * Pano parçalarının veri çekme katmanı. Canlıda (`live`, varsayılan) sorgu WS ile
 * güncel tutulur; `live: false` iken HTTP ile bir kez yüklenir (şablon önizlemesi).
 * Çizim bileşenleri bu hook'ları bilmez; yalnız `data` + `status` alır.
 */

export interface WidgetData<T> {
  data: T | undefined
  status: WidgetStatus
}

interface DataOptions {
  live?: boolean
}

/** Anahtar = sorgunun kendisi: filtresi bu parçaya dokunmayan değişiklik yeniden sorgulatmaz. */
function useWidgetQuery<T>(
  widget: DashboardWidget,
  query: Query,
  select: (result: ResultSet) => T,
  { live = true, enabled, compact }: DataOptions & { enabled?: boolean; compact?: boolean } = {},
) {
  return useCubeLiveQuery({
    queryKey: queryKeys.cube(widget.view, { ...query }),
    query,
    select,
    enabled,
    subscribe: live,
    compact,
  })
}

/** Döviz başına toplam satırları (TL ile USD toplanmaz). */
function useTotals(
  widget: TableWidget | ListWidget,
  measures: string[],
  state: ReportFilterState,
  options: DataOptions,
) {
  return useWidgetQuery(widget, totalsQuery(widget, measures, state), toRows, {
    ...options,
    enabled: measures.length > 0,
  })
}

export function useKpiData(
  widget: KpiWidget,
  state: ReportFilterState,
  options: DataOptions = {},
): WidgetData<KpiResult> & { fxFailed: boolean } {
  const result = useWidgetQuery(widget, kpiQuery(widget, state), toRows, options)
  const fx = useQuery({
    queryKey: ['fx', 'rates'],
    queryFn: fetchFxRates,
    enabled: widget.fx !== undefined,
    staleTime: 10 * 60_000,
  })
  const rate = widget.fx ? fx.data?.rates[widget.fx] : undefined
  const fxDate = fx.data?.date
  const rows = result.data
  const data = useMemo<KpiResult | undefined>(
    () =>
      rows && {
        ...kpiFromRows(widget, rows),
        ...(rate !== undefined && fxDate !== undefined ? { fx: { rate, date: fxDate } } : {}),
      },
    [rows, widget, rate, fxDate],
  )
  return {
    data,
    fxFailed: fx.isError,
    status: {
      isLoading: result.isLoading,
      isError: result.isError,
      error: result.error,
      busy: result.isPlaceholderData,
    },
  }
}

export function useTableData(
  widget: TableWidget,
  state: ReportFilterState,
  options: DataOptions = {},
): WidgetData<TableResult> {
  const rows = useWidgetQuery(widget, groupedTableQuery(widget, state), toRows, options)
  const totals = useTotals(widget, widget.totals ? widget.measures : [], state, options)
  const data = useMemo(
    () => rows.data && tableFromRows(rows.data, widget.totals ? totals.data : undefined),
    [rows.data, totals.data, widget.totals],
  )
  return {
    data,
    status: {
      isLoading: rows.isLoading,
      isError: rows.isError,
      error: rows.error,
      busy: rows.isPlaceholderData || totals.isPlaceholderData,
      onRetry: () => void rows.refetch(),
    },
  }
}

/** Canlı liste: sunucu taraflı sayfa + sıra; sonuçta yalnız geçerli sayfa vardır. */
export function useListData(
  widget: ListWidget,
  state: ReportFilterState,
  sort: ReportSortItem[],
  page: number,
  pageSize: number,
  options: DataOptions = {},
): WidgetData<ListResult> {
  const result = useWidgetQuery(
    widget,
    listPageQuery(widget, state, sort, page, pageSize),
    toTablePage,
    options,
  )
  const totals = useTotals(widget, widget.totals, state, options)
  const data = useMemo(
    () => result.data && listFromPage(result.data, totals.data),
    [result.data, totals.data],
  )
  return {
    data,
    status: {
      isLoading: result.isLoading,
      isError: result.isError,
      error: result.error,
      busy: result.isPlaceholderData || totals.isPlaceholderData,
      onRetry: () => void result.refetch(),
    },
  }
}

export function useChartData(
  widget: ChartWidget,
  state: ReportFilterState,
  options: DataOptions = {},
): WidgetData<ChartResult> {
  const rows = useWidgetQuery(widget, chartWidgetQuery(widget, state), toRows, options)
  return {
    data: rows.data && chartFromRows(rows.data),
    status: {
      isLoading: rows.isLoading,
      isError: rows.isError,
      error: rows.error,
      busy: rows.isPlaceholderData,
      onRetry: () => void rows.refetch(),
    },
  }
}

function toAgingRows(result: ResultSet, amount: string): ReportRow[] {
  return toRows(result).map((row) => ({ ...row, [amount]: toNumber(row[amount]) }))
}

export function useAgingData(
  widget: AgingWidget,
  state: ReportFilterState,
  options: DataOptions = {},
): WidgetData<ReportRow[]> {
  // Süzülen tüm ham kayıtlar gelir: compact biçim yükü yarıya indirir (docs/HANDOFF.md
  // 2026-10-19). Bu biçimde sayılar dizgi gelir; tutar burada bir kez sayıya çevrilir.
  const rows = useWidgetQuery(
    widget,
    agingQuery(widget, state),
    (result) => toAgingRows(result, widget.amount),
    { ...options, compact: true },
  )
  return {
    data: rows.data,
    status: {
      isLoading: rows.isLoading,
      isError: rows.isError,
      error: rows.error,
      busy: rows.isPlaceholderData,
      onRetry: () => void rows.refetch(),
    },
  }
}
